/**
 * Centraliserad plattformsdetektering för Vantastika.
 *
 * Skiljer mellan:
 *   - OS:        "ios" | "android" | "web"
 *   - Runtime:   "native" (Capacitor-app) | "pwa" (installerad PWA) | "browser"
 *
 * Använd `getPlatform()` i ren logik (lib/, edge-helpers) och
 * `usePlatform()` i React-komponenter.
 */

export type OS = "ios" | "android" | "web";
export type Runtime = "native" | "pwa" | "browser";

export interface PlatformInfo {
  os: OS;
  runtime: Runtime;
  isIOS: boolean;
  isAndroid: boolean;
  isWeb: boolean;
  isNative: boolean;
  isPWA: boolean;
  isBrowser: boolean;
  /** True om enheten är iOS eller Android (mobiloperativ) oavsett runtime. */
  isMobileOS: boolean;
}

const hasWindow = (): boolean => typeof window !== "undefined";

const isCapacitorNative = (): boolean => {
  if (!hasWindow()) return false;
  const cap = (window as unknown as { Capacitor?: { isNativePlatform?: () => boolean; getPlatform?: () => string } }).Capacitor;
  return cap?.isNativePlatform?.() === true;
};

const detectOS = (): OS => {
  if (!hasWindow()) return "web";

  // Capacitor exposes the real OS even inside the WebView.
  const cap = (window as unknown as { Capacitor?: { getPlatform?: () => string } }).Capacitor;
  const capPlatform = cap?.getPlatform?.();
  if (capPlatform === "ios") return "ios";
  if (capPlatform === "android") return "android";

  const ua = navigator.userAgent || "";
  const platform = (navigator as Navigator & { userAgentData?: { platform?: string } }).userAgentData?.platform || "";

  // iPadOS 13+ reports as Mac — disambiguate via touch points.
  const isIPad = /Macintosh/i.test(ua) && navigator.maxTouchPoints > 1;
  if (/iPad|iPhone|iPod/i.test(ua) || /iOS|iPadOS/i.test(platform) || isIPad) return "ios";
  if (/Android/i.test(ua) || /Android/i.test(platform)) return "android";

  return "web";
};

const detectRuntime = (os: OS): Runtime => {
  if (isCapacitorNative()) return "native";
  if (!hasWindow()) return "browser";

  // Detect installed PWA (display-mode: standalone) — also iOS Safari quirk.
  const standalone =
    window.matchMedia?.("(display-mode: standalone)").matches === true ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true;

  if (standalone && (os === "ios" || os === "android")) return "pwa";
  return "browser";
};

let cached: PlatformInfo | null = null;

export function getPlatform(): PlatformInfo {
  if (cached) return cached;
  const os = detectOS();
  const runtime = detectRuntime(os);
  cached = {
    os,
    runtime,
    isIOS: os === "ios",
    isAndroid: os === "android",
    isWeb: os === "web",
    isNative: runtime === "native",
    isPWA: runtime === "pwa",
    isBrowser: runtime === "browser",
    isMobileOS: os === "ios" || os === "android",
  };
  return cached;
}

/** Test-only: rensa cache (anropas inte i produktion). */
export function __resetPlatformCache(): void {
  cached = null;
}